import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useSite } from "@/contexts/SiteContext";
import { activeChannels, buildChannelHref } from "@/lib/contactChannels";
import ContactChannels, { metaFor, isAppOrWebLink } from "@/components/ContactChannels";

// Buton flotant de contact — multi-canal. Randează canalele configurate din
// Admin → Setări → Canale de contact (site.contact_channels). Dacă niciunul nu
// e activ, cade pe comportamentul istoric (site.contact.whatsapp_number).
export default function WhatsAppFloat() {
  const { site } = useSite();
  const [open, setOpen] = useState(false);

  const configured = activeChannels(site);
  let channels = configured;

  // Închide meniul la Escape (accesibilitate — comportament standard pentru
  // meniuri flotante deschise cu un buton).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  if (!channels.length) {
    const number = site?.contact?.whatsapp_number || "";
    if (number) {
      const fallback = {
        key: "whatsapp", type: "whatsapp", label: "WhatsApp", value: number,
        prefill: site?.contact?.whatsapp_message || "Salut! Vreau să programez o discuție.",
        enabled: true, order: 0,
      };
      const href = buildChannelHref(fallback);
      if (href) channels = [{ ...fallback, href }];
    }
  }

  if (!channels.length) return null;

  const primary = channels[0];
  // Sursă unică de adevăr pentru culoare/icon per tip de canal — același
  // CHANNEL_META folosit de <ContactChannels/>, ca să nu diverge stilul aici.
  const m = metaFor(primary.type);
  const PrimaryIcon = m.icon;

  // Un singur canal activ → butonul deschide direct canalul (comportament istoric).
  if (channels.length === 1) {
    return (
      <a
        href={primary.href}
        {...(isAppOrWebLink(primary.href) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        data-testid="whatsapp-float"
        aria-label={`Trimite mesaj pe ${primary.label || m.label}`}
        className="fixed bottom-6 right-6 z-30 flex items-center gap-2 px-4 py-3 rounded-full text-sm font-medium transition-all hover:scale-105"
        style={{
          background: m.bg,
          color: m.color,
          boxShadow: "0 14px 40px -10px rgba(0, 0, 0, 0.35)",
        }}
      >
        <PrimaryIcon size={20} />
        <span className="hidden sm:inline">{primary.label || m.label}</span>
      </a>
    );
  }

  // Mai multe canale active → butonul rotund deschide un mic meniu vertical.
  return (
    <div className="fixed bottom-6 right-6 z-30" data-testid="contact-float">
      {open && (
        <>
          <button
            type="button"
            aria-label="Închide meniul de contact"
            tabIndex={-1}
            className="fixed inset-0 z-20 cursor-default bg-transparent"
            onClick={() => setOpen(false)}
          />
          <div className="absolute bottom-[calc(100%+12px)] right-0 z-30 w-64" data-testid="contact-float-menu">
            <ContactChannels variant="stack" onNavigate={() => setOpen(false)} />
          </div>
        </>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="whatsapp-float"
        aria-label={open ? "Închide meniul de contact" : "Contactează-mă"}
        aria-expanded={open}
        className="relative z-30 flex items-center gap-2 px-4 py-3 rounded-full text-sm font-medium transition-all hover:scale-105"
        style={{
          background: m.bg,
          color: m.color,
          boxShadow: "0 14px 40px -10px rgba(0, 0, 0, 0.35)",
        }}
      >
        {open ? <X size={20} /> : <PrimaryIcon size={20} />}
        <span className="hidden sm:inline">{open ? "Închide" : "Contactează-mă"}</span>
      </button>
    </div>
  );
}
